import { ChatCircle, ThumbsUp } from "@phosphor-icons/react";
import { useQuery } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { RoomThread } from "../../shared/types.ts";
import { authorDisplay, replyAuthorDisplay } from "../lib/threads.ts";
import { useStaffSessionRoute } from "../lib/useStaffSessionRoute.ts";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { KeyThemesProjection, ListeningState } from "../../shared/types.ts";
import { KeyThemesStage } from "./KeyThemesStage.tsx";
import { deriveKeyThemesView, formatClock } from "./keyThemesView.ts";
import { KeyThemesBoardStage } from "./KeyThemesBoardStage.tsx";
import { deriveBoardView, type BoardRenderState } from "./keyThemesBoardView.ts";
import { ProjectorJoinStage } from "./ProjectorJoinStage.tsx";
import { deriveProjectedRepliesLayout, escalateReplyFit } from "./projectorThreadLayout.ts";

/** Fixed short link shown/encoded on the projector (per operator request). */
const PROJECTOR_JOIN_URL = "https://stla.one/comodo";

export function ProjectorView() {
  const sessionRoute = useStaffSessionRoute();
  const projectionArgs = sessionRoute.hasJoinCodeRoute
    ? sessionRoute.sessionId ? { sessionId: sessionRoute.sessionId } : "skip"
    : {};
  const projection = useQuery(convexApi.projector.getProjection, projectionArgs);
  const keyThemesMode =
    projection?.projector.mode === "key-themes" ||
    projection?.projector.mode === "key-themes-board";
  const keyThemes = useQuery(
    convexApi.panelRecaps.getKeyThemesProjection,
    keyThemesMode ? projectionArgs : "skip",
  );
  const listening = useQuery(
    convexApi.panelRecaps.getLiveListeningState,
    projection?.projector.mode === "key-themes" ? projectionArgs : "skip",
  );

  if (sessionRoute.notFound) {
    return <div className="projector-page"><div className="projector-empty" data-testid="staff-session-unavailable">Session not found.</div></div>;
  }
  if (sessionRoute.resolving || projection === undefined) {
    return <div className="projector-page"><div className="projector-empty">Loading…</div></div>;
  }
  if (!projection) {
    return <div className="projector-page"><div className="projector-empty">No active session.</div></div>;
  }

  const liveThemesView = deriveKeyThemesView(keyThemes);
  const emptyThemeMode =
    (projection.projector.mode === "key-themes" && liveThemesView.kind === "qr") ||
    (projection.projector.mode === "key-themes-board" &&
      keyThemes !== undefined &&
      (!keyThemes || keyThemes.boardThemes.length === 0));

  // The join splash is a complete page of its own — brand row and session
  // title live inside the stage, so the global header would duplicate them.
  if (projection.projector.mode === "join-qr" || emptyThemeMode) {
    return (
      <div className="projector-page mode-join-qr">
        <ProjectorJoinStage
          sessionTitle={projection.sessionTitle}
          panelists={projection.panelists ?? []}
        />
      </div>
    );
  }

  // Artifact projection was removed from the UI (spec §8); fall back to the board.
  const projectedThread = projection.projectedContent?.type === "thread" ? projection.projectedContent : null;

  return (
    <div className={`projector-page mode-${projection.projector.mode}`}>
      <header className="topbar projector-header">
        <div className="projector-header-line">
          <div className="brand">
            <span className="brand-mark" aria-hidden="true" />
          </div>
          <h1 className="participant-session-title">{projection.sessionTitle}</h1>
        </div>
        {projection.joinCode && (
          <div className="projector-join" data-testid="projector-join">
            <a className="projector-join-link" href={PROJECTOR_JOIN_URL}>
              stla.one/comodo
            </a>
          </div>
        )}
      </header>

      {projection.projector.mode === "key-themes" ? (
        keyThemesStage(liveThemesView, listening)
      ) : projection.projector.mode === "key-themes-board" ? (
        <KeyThemesBoardContainer view={keyThemes} />
      ) : projectedThread ? (
        <main className="projector-thread-stage">
          <ProjectorThreadStage
            thread={projectedThread.thread}
            showReplies={projectedThread.showReplies}
            highlightedReplyId={projectedThread.highlightReplyId}
          />
        </main>
      ) : (
        <main className="projector-board">
          {projection.boardThreads.length === 0 && <div className="projector-empty">No posts yet.</div>}
          {projection.boardThreads.map((thread) => {
            const author = authorDisplay(thread);
            return (
              <article className="projector-post" key={thread.id}>
                <p>{thread.body}</p>
                <footer>
                  {author.label && (
                    <span style={author.kind === "ai" ? { color: "var(--violet)" } : undefined}>
                      {author.kind === "ai" ? "✨ " : ""}{author.label}
                    </span>
                  )}
                  <span className="projector-stats">
                    <span className={`projector-stat ${thread.threadLikeCount > 0 ? "has-likes" : ""}`} title="Whole-thread upvotes"><ThumbsUp weight={thread.threadLikeCount > 0 ? "fill" : "bold"} />{thread.threadLikeCount}</span>
                    <span className="projector-stat" title="Replies"><ChatCircle weight="bold" />{thread.replyCount}</span>
                  </span>
                </footer>
              </article>
            );
          })}
        </main>
      )}
    </div>
  );
}

function KeyThemesBoardContainer({
  view,
}: {
  view: KeyThemesProjection | null | undefined;
}) {
  const renderStateRef = useRef<BoardRenderState | null>(null);
  const derived = useMemo(
    () =>
      view
        ? deriveBoardView({
            boardThemes: view.boardThemes,
            themeIdFallback: view.themeIdFallback,
            previous: renderStateRef.current,
          })
        : null,
    [view],
  );
  useEffect(() => {
    if (derived) renderStateRef.current = derived.nextState;
  }, [derived]);

  if (view === undefined) {
    return (
      <main className="projector-ktboard">
        <div className="ktb-empty">Loading…</div>
      </main>
    );
  }
  if (!view || view.boardThemes.length === 0) {
    return null;
  }
  return (
    <KeyThemesBoardStage
      cards={derived?.cards ?? []}
      footerLabel={view.themesConsolidatedAt !== null ? formatClock(view.themesConsolidatedAt) : null}
    />
  );
}

function keyThemesStage(
  view: ReturnType<typeof deriveKeyThemesView>,
  listening: ListeningState | null | undefined,
) {
  if (view.kind === "loading") {
    return (
      <main className="projector-keythemes">
        <div className="projector-empty">Loading…</div>
      </main>
    );
  }
  if (view.kind === "qr") {
    return null;
  }
  return <KeyThemesStage view={view} listening={listening} />;
}

function ProjectorThreadStage({
  thread,
  showReplies,
  highlightedReplyId,
}: {
  thread: RoomThread;
  showReplies: boolean;
  highlightedReplyId?: string;
}) {
  const author = authorDisplay(thread);
  const layout = deriveProjectedRepliesLayout({
    replies: thread.replies ?? [],
    showReplies,
    highlightReplyId: highlightedReplyId,
    replyCount: thread.replyCount,
  });
  const [fitLevel, setFitLevel] = useState(0);
  const [overflowing, setOverflowing] = useState(false);
  const repliesRef = useRef<HTMLDivElement | null>(null);
  const fit = escalateReplyFit(layout, fitLevel);

  // Re-fit from the count baseline when the content or the window changes.
  useEffect(() => {
    const reset = () => setFitLevel(0);
    window.addEventListener("resize", reset);
    return () => window.removeEventListener("resize", reset);
  }, []);
  useEffect(() => {
    setFitLevel(0);
  }, [thread.replies]);

  // Measure after every layout pass: escalate while overflowing, then fade.
  // Deliberately dep-less — it must re-measure after each escalation render;
  // the state setters below are no-ops once the fit has converged.
  useLayoutEffect(() => {
    const el = repliesRef.current;
    if (!el) {
      setOverflowing(false);
      return;
    }
    const overflow = el.scrollHeight > el.clientHeight + 1;
    if (overflow && fit.canEscalate) {
      setFitLevel((level) => level + 1);
      return;
    }
    setOverflowing(overflow);
  });
  return (
    <section className="projector-thread-preview" data-testid="projector-thread-preview">
      <span className="projector-label">{author.kind === "ai" ? "Discussion starter" : "Audience question"}</span>
      <div className="projector-thread-meta">
        {author.label && (
          <span style={author.kind === "ai" ? { color: "var(--violet)" } : undefined}>
            {author.kind === "ai" ? "✨ " : ""}{author.label}
          </span>
        )}
        <span className="projector-stats">
          <span className={`projector-stat ${thread.likeCount > 0 ? "has-likes" : ""}`} title="Likes"><ThumbsUp weight={thread.likeCount > 0 ? "fill" : "bold"} />{thread.likeCount}</span>
          <span className="projector-stat" title="Replies"><ChatCircle weight="bold" />{thread.replyCount}</span>
        </span>
      </div>
      <p>{thread.body}</p>
      {layout.replies.length > 0 && (
        <div
          className="projector-thread-replies"
          data-tier={fit.tier}
          data-columns={fit.columns}
          data-overflowing={overflowing || undefined}
          ref={repliesRef}
        >
          {layout.replies.map((reply) => {
            const replyAuthor = replyAuthorDisplay(reply.authorLabel, reply.authorIdentityKind);
            return (
              <div
                className={`projector-thread-reply ${reply.id === highlightedReplyId ? "is-highlighted" : ""}`}
                data-testid="projector-thread-reply"
                key={reply.id}
              >
                {replyAuthor.label && <strong>{replyAuthor.label}</strong>}
                <span>{reply.body}</span>
              </div>
            );
          })}
        </div>
      )}
      {layout.hiddenReplyCount > 0 && (
        <div className="projector-replies-more" data-testid="projector-replies-more">
          +{layout.hiddenReplyCount} more {layout.hiddenReplyCount === 1 ? "reply" : "replies"}
        </div>
      )}
    </section>
  );
}
